import Styles from './NodeTerminal.module.scss';
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// module imports
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-chat-widget/lib/styles.css';
import { Avatar, Typography, Tooltip, Fab, LinearProgress, Box, Backdrop } from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { pink } from '@mui/material/colors';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import VisibilityIcon from '@mui/icons-material/Visibility';

// component imports
import { useAPI, AuthContext, Feedback, ViewContext, useCan } from 'components/lib';
import { HelperChat } from 'components/global/HelperChat/HelperChat';
import InfoDrawer from 'components/global/NodeLayout/InfoDrawer';
import { VideoPopup } from 'components/global/VideoPopup/VideoPopup';
import { NodeTimeline } from 'components/global/NodeLayout/NodeTimeline';
import { LightTooltip } from 'components/global/NodeLayout/NodeTopostman';
import { TerminalPane } from 'components/global/NodeLayout/TerminalPane';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Joyride } from 'components/walkthrough/walkthrough';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';
import { BackConfirmationCard } from 'components/global/BackConfirmationCard/BackConfirmationCard';
import RevealAnswerKey from 'components/RevealAnswerKey/RevealAnswerKey';

// util imports
import { decoder } from 'utils/helpers/decoder';
import { createSocketConnection, cleanupSocket } from 'views/CodeEditor/utils/socketService';
import { copyEnabledChecker } from 'utils/helpers/utility';

// asset imports
import Soundcheck from 'assets/sounds/try-again.mp3';
import { InstructionPane } from 'components/global/InstructionPane/InstructionPane';
import ContainerLoader from 'components/ContainerLoader/ContainerLoader';

const videoSrc = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/media/git/video/github_signup.mp4`;
const finalAudio = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/media/git/voice/finish_node.mp3`;

export function NodeTerminal(props) {
	const { node_id } = useParams();
	const { lab_id } = useParams();
	const { resource } = useParams();
	const [currentStep, setCurrentStep] = useState(0);
	const [userInput, setUserInput] = useState('');
	const [steps, setSteps] = useState();
	const context = useContext(AuthContext);

	const { data, loading } = useAPI(`/api/labs/${lab_id}/nodes/${node_id}/${resource}`, 'GET');

	const [nodeFinished, setNodeFinished] = useState(false);
	const [isMuted, setIsMuted] = useState(true);
	const [substepCount, setSubstepCount] = useState(0);
	const [drawerOpen, setDrawerOpen] = useState(false);

	const [nodeInfo, setNodeInfo] = useState();
	// flag for the timer tooltip
	const [showTooltip, setShowTooltip] = useState(false);
	// flag for info pop up
	const [showInfo, setShowInfo] = useState(true);
	const [isloading, setIsloading] = useState(false);

	// const [containerID, setContainerID] = useState('');
	const [startLab, setStartLab] = useState(true);
	const [loadingContainerStart, setLoadingContainerStart] = useState(false);
	const [progress, setProgress] = useState(0);
	const [displayData, setDisplayData] = useState();
	const [copied, setCopied] = useState(false);

	const sessionId = localStorage.getItem('sessionId');
	const [showFinishConfirm, setShowFinishConfirm] = useState(false);
	const [showReloadConfirm, setShowReloadConfirm] = useState(false);
	const [socketInstance, setSocketInstance] = useState(null);
	const [showContainerError, setShowContainerError] = useState(false);
	const roundedPercentage = nodeFinished ? 100 : Math.ceil(currentStep / nodeInfo?.numOfSteps * 100);
	const isCopyEnabled = copyEnabledChecker(context?.user);

	const [showReveal, setShowReveal] = useState(false);
	const [maxUnlocked, setMaxUnlocked] = useState(0);
	const audioRef = useRef(null);
	const finalAudioRef = useRef(null);
	const viewContext = useContext(ViewContext)

	useEffect(() => {
		setMaxUnlocked(prev => Math.max(prev, currentStep));
	}, [currentStep]);

	const isSupervisor = useCan('node.progress.flag');
	const isAssessmentNode = nodeInfo?.is_assessment;
	const canReveal = !!(isAssessmentNode && isSupervisor);

	const navigateBack = () => {
		navigate(-1);
	};
	const activityTimerRef = useRef(null);

	const startActivityTimer = () => {
		activityTimerRef.current = setTimeout(handleInactivity, 900000);
	};

	const resetActivityTimer = () => {
		clearTimeout(activityTimerRef.current);
		startActivityTimer();
	};

	function handleInactivity() {
		setShowReloadConfirm(true);
		handleContainerDelete();
	}

	const handleDrawer = () => {
		resetActivityTimer();
		setDrawerOpen(!drawerOpen);
	};

	const showChallengeInfo = () => {
		setShowInfo(true);
	};

	const hideChallengeInfo = () => {
		setShowInfo(false);
	};

	const currentRef = useRef(null);
	const navigate = useNavigate();
	const handleNext = () => {
		setIsloading(true);
		const formData = {
			sessionId: sessionId,
		};
		const options = {
			method: 'POST',
			url: `/api/user/labs/${lab_id}/nodes/${node_id}/end`,
			headers: {
				accept: 'application/json',
			},
			data: {
				...formData,
				...(nodeInfo.is_assessment === 1 && { completionPercent: roundedPercentage }),
			},
		};

		axios
			.request(options)
			.then(async function (response) {
				await new Promise((resolve) => setTimeout(resolve, 50)); // Adding a wait for api post to happen
				if (nodeInfo.is_assessment === 1) {
					navigate(`/labs/${lab_id}/nodes`);
				} else {
					navigate(`/labs/${lab_id}/nodes/${node_id}/quiz/attempt`);
				}
			})
			.catch((err) => {
				let status = err.response.status;
				console.log('error', status);
				navigate('/oops');
			});
	};

	const handleContainerDelete = () => {
		if (socketInstance) {
			socketInstance.disconnect();
		}
		const sessId = localStorage.getItem('sessionId');
		const options = {
			method: 'DELETE',
			url: `/api/container-instances`,
			data: { sessionId: sessId },
			headers: {
				accept: 'application/json',
			},
		};

		axios
			.request(options)
			.then(async function (response) {
				localStorage.removeItem('sessionId');
			})
			.catch((err) => {
				console.log(err)
			});

	};

	const handleRefresh = () => {
		window.location.reload();
	};

	const handleGoBack = () => {
		navigate(-1);
	};

	useEffect(() => {
		if (loadingContainerStart) {
			const interval = setInterval(() => {
				setProgress((prevProgress) => (prevProgress >= 95 ? 100 : prevProgress + 1.5));
			}, 1000);

			if (!loadingContainerStart) {
				clearInterval(interval);
			}
			return () => {
				clearInterval(interval);
			};
		}
	}, [loadingContainerStart]);

	// move to next step in the nodes
	function moveToNext() {
		if (currentStep !== nodeInfo.numOfSteps - 1) {
			setCurrentStep(currentStep + 1);
			var myElement = document.getElementById('currenttimelineHolder');
			var topPos = myElement.offsetTop;
			document.getElementById('timelinescrolldiv').scrollTop = topPos;
			setSubstepCount(0);
		} else {
			setNodeFinished(true);
		}
	}
	// Through an error while regex not matched
	function moveToError(current_regex) {
		const wastedAttemptAudio = new Audio(Soundcheck);
		wastedAttemptAudio.play();
		toast.dark(props.t('Labs.Nodes.NodeTerminal.toast.incorrect'), {
			type: 'error',
			position: 'top-right',
			autoClose: 3000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
		});
	}


	useEffect(() => {
		if (data) {
			const jsonData = decoder(data);
			setNodeInfo(jsonData);
			//below setting the numOfSteps from jsonData
			jsonData.numOfSteps = jsonData.steps.steps.length;
			setSteps(jsonData.steps.steps);
			setDisplayData(jsonData.content.reference_data);
			document.title = props.t('Labs.Nodes.NodeTerminal.title', { nodeName: jsonData.name })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	useEffect(() => {
		if (!steps) return;

		if (audioRef.current) {
			audioRef.current.pause();
		}

		const audioLink = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}${steps[currentStep]?.voice}`;
		if (audioLink && !isMuted) {
			const newAudio = new Audio(audioLink);
			audioRef.current = newAudio;
			newAudio.play();
		}

		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, isMuted]);

	useEffect(() => {
		if (!nodeFinished) return;

		if (audioRef.current) {
			audioRef.current.pause();
		}

		const audio = new Audio(finalAudio);
		finalAudioRef.current = audio;
		audio.play();

		viewContext.notification.show(
			props.t('Labs.Nodes.NodeTerminal.end'),
			'info',
			false
		);

		return () => {
			if (finalAudioRef.current) {
				finalAudioRef.current.pause();
			}
		};

	}, [nodeFinished])

	// when input comes from terminal
	useEffect(() => {
		// only the node with array flag true node will falls into that
		if (nodeInfo?.array_flag) {
			if (steps) {
				const current = steps[currentStep].exp_input;
				const len = current.length;
				const current_regex = new RegExp(current[substepCount]);
				if (current_regex.test(userInput.trim()) && userInput) {
					setSubstepCount(substepCount + 1);
					if (len === substepCount + 1) {
						// call function to pass to next step
						moveToNext(steps);
					}
				} else {
					// call function to report the error
					moveToError(current_regex);
				}
			}
		} else {
			if (steps) {
				const current_regex = new RegExp(steps[currentStep].exp_input);
				if (current_regex.test(userInput.trim()) && userInput) {
					// call function to pass to next step
					moveToNext(steps);
				} else {
					// call function to report the error
					moveToError(current_regex);
				}
			}
		} // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userInput]);

	const handleVoiceClick = () => {
		resetActivityTimer();
		setIsMuted(!isMuted);
	};

	const startLabClick = async () => {
		startActivityTimer();
		setLoadingContainerStart(!loadingContainerStart);
		const formData = {
			resource: nodeInfo.resource,
		};
		const options = {
			method: 'POST',
			url: `/api/user/labs/${lab_id}/nodes/${node_id}/start`,
			headers: {
				accept: 'application/json',
			},
			data: formData,
		};
		await axios.request(options)
			.then((response) => {
				new Promise((resolve) => setTimeout(resolve, 100));
				setLoadingContainerStart(false);
				const socket = createSocketConnection(
					process.env.REACT_APP_RELAY_URL,
					context.user?.token,
					() => { setShowContainerError(true) }
				);
				setSocketInstance(socket);
				setStartLab(false);
				localStorage.setItem('sessionId', response.data.session_id);
				if (context?.user?.stop_walkthrough?.includes(nodeInfo?.resource?.lab_resource_id) || !!nodeInfo.is_assessment) {
					setIsMuted(!isMuted)
				}
			}).catch((err) => {
				if (err.response.status === 403) {
					navigate('/nopermission');
				} else {
					navigate('/oops');
				}
			});
	};

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			cleanupSocket();
		};
	}, []);

	useEffect(() => {
		if (socketInstance) {
			socketInstance.on('disconnect', (reason) => {
				// Show reconnection message to user
				toast.dark('Connection lost. Attempting to reconnect...', {
					type: 'warning',
					position: 'top-right',
					autoClose: 3000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
				});

				if (reason === 'io server disconnect') {
					socketInstance.connect();
				}
			});

			socketInstance.on('reconnect', () => {
				toast.success('Reconnected successfully!');
			});

			socketInstance.on('connect_error', () => {
				toast.dark('Failed to reconnect. Please refresh the page.', {
					type: 'error',
					position: 'top-right',
					autoClose: 3000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
				});
			});
		}

		return () => {
			if (socketInstance) {
				socketInstance.off('disconnect');
				socketInstance.off('reconnect');
				socketInstance.off('connect_error');
			}
		};
	}, [socketInstance]);


	const sendHeartbeat = () => {
		const formData = {
			node_id: node_id,
		};
		const options = {
			method: 'POST',
			url: `/api/container-instances/heartbeat`,
			headers: {
				accept: 'application/json',
			},
			data: formData,
		};

		axios
			.request(options)
			.then(function (response) {
				// navigate(`/`);
			})
			.catch((err) => {
				console.log(err.response.status);
			});
	};

	useEffect(() => {
		//send heartbeat call every 4 mins
		const heartBeatId = setInterval(sendHeartbeat, 240000);

		return () => {
			clearInterval(heartBeatId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const resetWalkthrough = (data) => {
		if (data.status === 'finished' | data.action === 'skip' | data.action === 'tour:end') {
			setIsMuted(!isMuted)
		}
	}
	useEffect(() => {
		const resourceIncluded = context?.user?.stop_walkthrough?.includes(nodeInfo?.resource?.lab_resource_id);
		if (resourceIncluded && !startLab) {
			setIsMuted(!isMuted)

		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [context?.user?.stop_walkthrough])

	const showConfirm = () => {
		setShowFinishConfirm(true);
	}

	const handleConfirmClick = () => {
		handleNext();
	}

	const handleCopyButton = async () => {
		try {
			await navigator.clipboard.writeText(steps[currentStep]?.command);
			setCopied(true);
			setTimeout(() => {
				setCopied(false);
			}, 2000);
			toast.dark(props.t('Labs.Nodes.NodeAutomation.toast.warn'), {
				type: 'warning',
				position: 'top-right',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
			});
		} catch (error) {
			toast.dark(props.t('Labs.Nodes.NodeAutomation.copyCode.alert'), {
				type: 'error',
				position: 'top-right',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
			});
		}
	}

	const handleCopy = async (e) => {
		setCopied(true);
		setTimeout(() => {
			setCopied(false);
		}, 2000);
		toast.dark(props.t('Labs.Nodes.NodeAutomation.toast.warn'), {
			type: 'warning',
			position: 'top-right',
			autoClose: 3000,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
		});
	};

	const handleCopyDisabled = (e) => {
		e.preventDefault();
	}


	return (
		<>
			<LoadingBackdrop isOpen={isloading} />

			<ContainerLoader 
				progress={progress} 
				loadingContainerStart={loadingContainerStart} 
				containerHints={nodeInfo?.container_loader_json} 
			/>
			
			<ToastContainer
				id="warning-toast"
				position="top-right"
				autoClose={5000}
				hideProgressBar={false}
				newestOnTop
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
				limit={3}
			/>
			{loading ? (
				<div>
					<LoadingBackdrop isOpen={true} />
				</div>
			) : steps ? (
				<>
					<ConfirmationCard
						title={props.t("Labs.Nodes.NodeTerminal.confirm.timeout.title")}
						body={props.t("Labs.Nodes.NodeTerminal.confirm.timeout.body")}
						showCard={showReloadConfirm}
						setShowCard={setShowReloadConfirm}
						handleCancel={handleGoBack} // Pass the refresh handler
						handleConfirm={handleRefresh} // Pass the go back handler
						confirmButtonText={props.t('Labs.Nodes.NodeTerminal.confirm.button.refresh')}
						cancelButtonText={props.t('Labs.Nodes.NodeTerminal.confirm.button.goBack')}
					/>
					<ConfirmationCard
						title={nodeInfo.is_assessment ? props.t("Labs.Nodes.NodeTerminal.confirm.start.title.assessment") : props.t("Labs.Nodes.NodeTerminal.confirm.start.title.default")}
						body={props.t("Labs.Nodes.NodeTerminal.confirm.start.body")}
						showCard={startLab}
						setShowCard={setStartLab}
						handleCancel={navigateBack}
						handleConfirm={startLabClick}
					/>

					<ConfirmationCard
						title={props.t('Labs.Nodes.NodeTerminal.error.connection.title')}
						body={props.t('Labs.Nodes.NodeTerminal.error.connection.body')}
						showCard={showContainerError}
						setShowCard={setShowContainerError}
						handleCancel={navigateBack}
						handleConfirm={handleRefresh}
						confirmButtonText={props.t('Labs.Nodes.NodeTerminal.confirm.button.refresh')}
						cancelButtonText={props.t('Labs.Nodes.NodeTerminal.confirm.button.goBack')}
					/>
					<BackConfirmationCard
						isSaveAvailable={false}
						isContainerLab={true}
						sessionId={sessionId}
					/>
					{!startLab && nodeInfo.is_assessment !== 1 ? (
						<Joyride
							open={nodeInfo?.resource?.walkthrough?.open}
							run={nodeInfo?.resource?.walkthrough?.run}
							steps={nodeInfo?.resource?.walkthrough?.steps || []}
							resourceId={nodeInfo?.resource?.lab_resource_id}
							disableOverlay={true}
							callback={resetWalkthrough}
						/>
					) : null}

					<VideoPopup
						showInfo={showInfo && nodeInfo.name === 'Remote-GitHub'}
						hideChallengeInfo={() => {
							hideChallengeInfo();
							setShowTooltip(true);
						}}
						videoSrc={videoSrc}
					/>

					<div id="ancestorContainer" className={Styles.ancestorContainer}>
						<div id="titleContainer" className={Styles.titleContainer}>
							<Tooltip
								title={props.t("Labs.Nodes.NodeTerminal.tooltip.video")}
								arrow
								placement="top"
								open={showTooltip}
								PopperProps={{
									style: { display: nodeInfo.name === 'Remote-GitHub' ? 'block' : 'none' },
								}}
							>
								<div
									style={{ display: 'flex', cursor: nodeInfo.name === 'Remote-GitHub' ? 'pointer' : '' }}
									onClick={() => showChallengeInfo()}
									onMouseEnter={() => setShowTooltip(true)}
									onMouseLeave={() => setShowTooltip(false)}
								>
									<Avatar
										sx={{
											bgcolor: pink[500],
											alignSelf: 'center',
											mr: 1,
											width: 55,
											height: 55,
										}}
									>
										<TerminalIcon sx={{ width: 42, height: 42 }} />
									</Avatar>
									<Typography
										variant="h3"
										sx={{
											textTransform: 'uppercase',
											color: '#FFFFFF',
										}}
									>
										{nodeInfo.name}
									</Typography>
								</div>
							</Tooltip>
							<div id="finishButtonContainer" className={Styles.finishButtonContainer}>
								<span style={{ display: 'flex', flexDirection: 'row' }}>
									<LightTooltip
										title={nodeFinished ? props.t("Labs.Nodes.NodeTerminal.tooltip.finish.node") : props.t("Labs.Nodes.NodeTerminal.tooltip.finish.assessment")}
										arrow
										placement="left"
										classes={{
											tooltip: {
												backgroundColor: '#fff',
												color: 'rgba(0, 0, 0, 0.87)',
												boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
												fontSize: 11,
											},
										}}
									>
										<span>
											<button
												variant="extended"
												className={Styles.finishButton}
												onClick={() => {
													nodeInfo.is_assessment ? showConfirm() : handleNext()
												}}
												disabled={
													!nodeInfo.is_assessment
														? !nodeFinished
														: roundedPercentage < 70
												}
											>
												<KeyboardDoubleArrowRightIcon />
												{props.t('Labs.Nodes.NodeTerminal.button')}
											</button>
										</span>
									</LightTooltip>
									<ConfirmationCard
										title={props.t('Labs.Nodes.NodeTerminal.confirm.completed.title')}
										body={props.t('Labs.Nodes.NodeTerminal.confirm.completed.body')}
										showCard={showFinishConfirm}
										setShowCard={setShowFinishConfirm}
										handleConfirm={handleConfirmClick}
									/>
								</span>

								{canReveal && (
									<div
										id="revealAnswer"
										onClick={() => setShowReveal(true)}
										style={{ marginRight: 8 }}
									>
										<LightTooltip
											title={props.t('Labs.Nodes.NodeTerminal.tooltip.revealAnswer')}
											arrow
											placement="left"
											classes={{
												tooltip: {
													backgroundColor: '#fff',
													color: 'rgba(0, 0, 0, 0.87)',
													boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
													fontSize: 11,
												},
											}}
										>
											<Fab color="primary" size="medium">
												<VisibilityIcon />
											</Fab>
										</LightTooltip>
									</div>
								)}

								<div id="infoDrawer" onClick={handleDrawer}>
									<LightTooltip
										title={props.t('Labs.Nodes.NodeTerminal.tooltip.reference')}
										arrow
										placement="left"
										classes={{
											tooltip: {
												backgroundColor: '#fff',
												color: 'rgba(0, 0, 0, 0.87)',
												boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
												fontSize: 11,
											},
										}}
									>
										<Fab color="primary" size="medium">
											<LightbulbIcon />
										</Fab>
									</LightTooltip>
								</div>
							</div>
						</div>
						<div id="midContainer" className={Styles.midContainer}>
							<div id="terminalContainer" className={Styles.terminalContainer}>
								<TerminalPane socket={socketInstance} setUserInput={setUserInput} sessionId={sessionId} resetActivityTimer={resetActivityTimer} />
							</div>
							<div data-testid="nodeTerminal-midContainer-instructionsContainer" id="instructionsContainer" className={Styles.instructionsContainer}>
								<InstructionPane
									props={props}
									steps={steps}
									currentStep={currentStep}
									isMuted={isMuted}
									copied={copied}
									showLineNumbers={false}
									setIsMuted={setIsMuted}
									isCopyEnabled={isCopyEnabled}
									onHandleCopy={handleCopy}
									onHandleVoiceClick={handleVoiceClick}
									onHandleCopyDisabled={handleCopyDisabled}
									onHandleCopyButton={handleCopyButton}
									nodeInfo={nodeInfo}
									theme="light"
								/>
							</div>
							<div data-testid="nodeTerminal-midContainer-Timeline" id="Timeline" className={Styles.Timeline}>
								<NodeTimeline
									nodeFinished={nodeFinished}
									currentStep={currentStep}
									steps={steps}
									// hideToolTip={true}
									collapsable={true}
									currentRef={currentRef}
									hideToolTip={!!nodeInfo.is_assessment}
									setCurrentStep={setCurrentStep}
									maxUnlocked={maxUnlocked}
								/>
							</div>
						</div>

					</div>

					<RevealAnswerKey
						showAnswerKeyDialog={showReveal}
						setShowAnswerKeyDialog={setShowReveal}
						steps={steps}
						currentStep={currentStep}
						language={"bash"}
						nodeId={node_id}
						nodeName={nodeInfo?.name}
						labTitle={nodeInfo?.labName}
					/>
					{(!startLab && !nodeFinished && nodeInfo.is_assessment !== 1 && nodeInfo?.resource?.config?.chatbotTopic) && (
						<HelperChat
							active={props.aiChat}
							topic={nodeInfo?.resource?.config?.chatbotTopic}
						/>
					)}
					<InfoDrawer
						handleDrawer={handleDrawer}
						drawerOpen={drawerOpen}
						titleText={props.t('Labs.Nodes.NodeTerminal.reference', { LabName: nodeInfo.labName })}
						displayData={displayData}
					/>

					{nodeFinished && <Feedback isInNode={true} nodeName={nodeInfo.name} labName={nodeInfo.labName} />}
				</>
			) : (
				<div>
					<LoadingBackdrop isOpen={true} />
				</div>
			)}
		</>
	);
}
